/**
 * The transcript — scroll container + autoscroll, over the clean projection
 * (TranscriptMessage[]). Replaces the legacy AgentPanelContent + MessageList
 * (which rendered the fat agentPanelStore model). Pairs each tool_use with its
 * tool_result (by toolUseId) so results fold into their call, and drops the
 * standalone tool_result messages from the flow.
 */

import type { TranscriptMessage } from '@mod/core/model'
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useProjectFolderPath } from '../../services/code/projectFolder'
import type { CommentLookup } from './CommentInThread'
import { OpenRef } from './Markdown'
import { RunFilesSummary } from './RunFilesSummary'
import { cardsAfterRows, projectRunFileChanges } from './runFileChanges'
import type { StopTrailing } from './stopVerdict'
import type { SubthreadState } from './subthreadState'
import { ThreadRowView } from './ThreadRowView'
import type { CardTarget } from './threadCards'
import { buildThreadRows, toolResults } from './threadRows'

export function Thread({
  transcript,
  header,
  onOpenDocument,
  onOpenSession,
  resolveTitle,
  resolveSessionState,
  renderUndo,
  commentLookup,
  emptyText = 'No messages yet. Say something below.',
  stopTrailing,
  projectId,
  sourceId,
  fileChanges,
}: {
  transcript: TranscriptMessage[]
  /** What an empty thread says. A guest whose access is not settled yet sees
   *  that, not an invitation to type into a session it cannot write. */
  emptyText?: string
  /** Rendered at the TOP of the scroll content (e.g. a "Load earlier" control) so
   *  it only appears when the user scrolls up, and disappears when omitted. */
  header?: ReactNode
  /** Open a document a card points at. Absent leaves cards drawn but flat: a
   *  transcript rendered in a slot with no layout behind it should still show
   *  WHAT the turn produced, and simply not offer to go there. */
  onOpenDocument?: (documentId: string) => void
  /** Open a session a card points at — one the agent started, or the one that
   *  reported back. */
  onOpenSession?: (sessionId: string) => void
  /** The current title of a carded thing, from the project registry, so a
   *  document renamed since it was written reads by its name now. */
  resolveTitle?: (target: CardTarget, id: string) => string | undefined
  /** RFC 0302 S5: what the subthread under a post is doing, so a session card
   *  changes in place from working, to waiting for you, to done. */
  resolveSessionState?: (sessionId: string) => SubthreadState | undefined
  /** RFC 0301 S9: the undo on an edit card. Absent, cards offer none. */
  renderUndo?: (documentId: string, hash: string) => ReactNode
  /** RFC 0324 3d: a comment's writer and words, from the project, so a comment
   *  in the transcript shows who wrote it and the agent's reply says to whom. */
  commentLookup?: CommentLookup
  /** RFC 0348 3e: what the World agent decided about each stop. */
  stopTrailing?: StopTrailing
  projectId?: string
  sourceId?: string
  /** Declared by an installed app; absent means there are no file cards. */
  fileChanges?: { title: string; initialRows: number } | null
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const folder = useProjectFolderPath(projectId, sourceId)
  /** The content INSIDE the scroller: the box that actually grows. */
  const contentRef = useRef<HTMLDivElement>(null)
  const firstScroll = useRef(true)
  const prevHeight = useRef<number | null>(null)
  // Whether the user is pinned to the bottom, sampled on scroll (BEFORE the next
  // transcript growth) so the autoscroll effect knows their intent.
  const atBottom = useRef(true)

  // toolUseId -> its result, built from tool_result messages.
  const results = useMemo(() => toolResults(transcript), [transcript])

  // What is actually drawn: text rows, cards for the calls and events that
  // reached something, and runs of consecutive ordinary tool calls collapsed
  // into one row each (results fold into their call).
  //
  // `results` is passed rather than read inside, because a card has to know
  // whether the call came back and what id it produced. Keyed on the map
  // IDENTITY, which changes only when the transcript does, so this is one
  // rebuild per transcript change and not one per render.
  const rows = useMemo(
    () => buildThreadRows(transcript, (id) => results.get(id)),
    [transcript, results],
  )
  const runChanges = useMemo(
    () => (fileChanges ? projectRunFileChanges(transcript) : []),
    [transcript, fileChanges],
  )
  // Each card goes after the last row its own turn created (`row.at` is the
  // index of the message that made the row), so whatever is drawn before the
  // next prompt, a comment or a queued message, lands below the card.
  const after = useMemo(
    () => cardsAfterRows(rows, runChanges),
    [runChanges, rows],
  )
  /**
   * Where a link in a message goes when it names a thing rather than a url
   * (an `@` from the composer). The id alone does not say whether it is a
   * session or a document, and the project does: a session it lists opens as
   * a thread, anything else as a document.
   */
  const openRef = useMemo(() => {
    if (!onOpenDocument && !onOpenSession) return null
    return (id: string) => {
      if (onOpenSession && resolveTitle?.('session', id) !== undefined)
        onOpenSession(id)
      else onOpenDocument?.(id)
    }
  }, [onOpenDocument, onOpenSession, resolveTitle])

  const BOTTOM_SLOP = 160
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    atBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLOP
  }

  // On transcript growth: first render pins to the newest message. After that,
  // follow the bottom ONLY if the user was already there; otherwise KEEP their
  // viewport steady by absorbing the height delta — so "Load earlier" (which
  // prepends older messages while scrolled up) doesn't yank the view to the bottom.
  const follow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (firstScroll.current) {
      el.scrollTop = el.scrollHeight
      firstScroll.current = false
    } else if (atBottom.current) {
      el.scrollTop = el.scrollHeight
    } else if (prevHeight.current != null) {
      el.scrollTop += el.scrollHeight - prevHeight.current
    }
    prevHeight.current = el.scrollHeight
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run as the transcript grows
  useEffect(follow, [transcript.length, follow])

  /**
   * FOLLOW THE CONTENT, not just the message COUNT.
   *
   * An agent answering does not add a message per token: it appends to the one
   * it is already writing, and folds each tool result into the call above it.
   * Neither changes `transcript.length`, so the effect above never fired and the
   * reply grew off the bottom of the pane while you watched the top of it — the
   * whole point of a live transcript, missing.
   *
   * A ResizeObserver on the CONTENT (not the scroller, whose box is fixed)
   * catches every one of those: streamed text, an expanding tool block, a code
   * fence that reflows, an image that finally loaded. It re-uses the same rule,
   * so being scrolled up still means "leave me where I am".
   */
  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => follow())
    ro.observe(content)
    return () => ro.disconnect()
  }, [follow])

  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-muted-foreground text-sm">
        {emptyText}
      </div>
    )
  }

  return (
    <OpenRef.Provider value={openRef}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        // overflow-x-hidden is a BACKSTOP, not the fix: the wrapping rules in
        // Markdown.tsx are. It guarantees one stray token can never make the
        // whole thread scroll sideways, and fenced code blocks are unaffected
        // because they carry their own overflow-x-auto.
        className="scrollbar-project h-full overflow-y-auto overflow-x-hidden"
      >
        <div
          ref={contentRef}
          className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-4 py-4"
        >
          {header}
          {rows.map((row) => (
            <div key={row.key} className="contents">
              <ThreadRowView
                row={row}
                results={results}
                {...(onOpenDocument ? { onOpenDocument } : {})}
                {...(onOpenSession ? { onOpenSession } : {})}
                {...(resolveTitle ? { resolveTitle } : {})}
                {...(resolveSessionState ? { resolveSessionState } : {})}
                {...(renderUndo ? { renderUndo } : {})}
                {...(commentLookup ? { commentLookup } : {})}
                {...(stopTrailing ? { stopTrailing } : {})}
              />
              {(after.get(row.key) ?? []).map((run) => (
                <RunFilesSummary
                  key={run.key}
                  run={run}
                  title={fileChanges?.title}
                  initialRows={fileChanges?.initialRows}
                  folder={folder}
                  sourceId={sourceId}
                  {...(sourceId && onOpenDocument ? { onOpenDocument } : {})}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </OpenRef.Provider>
  )
}
